import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchRedditRankingApi,
  fetchRedditHistoryApi,
  fetchRedditSubredditsApi,
  fetchRedditPostsApi,
} from './redditApi';

/**
 * Redux-Slice für die Reddit-Erwähnungen.
 *
 * Das Ranking zeigt die aktuell meistdiskutierten Ticker (jüngster Snapshot),
 * der Verlauf je Ticker die selbst aufgebaute Historie (Erwähnungen/Upvotes
 * über die Zeit). Beides kommt aus der DB über den aktien-service.
 */

export const fetchRedditRanking = createAsyncThunk('reddit/fetchRanking', async () => {
  return (await fetchRedditRankingApi()) ?? [];
});

export const fetchRedditHistory = createAsyncThunk('reddit/fetchHistory', async (ticker) => {
  const verlauf = (await fetchRedditHistoryApi(ticker)) ?? [];
  return { ticker, verlauf };
});

export const fetchRedditSubreddits = createAsyncThunk('reddit/fetchSubreddits', async (ticker) => {
  return (await fetchRedditSubredditsApi(ticker)) ?? [];
});

export const fetchRedditPosts = createAsyncThunk('reddit/fetchPosts', async (ticker) => {
  return (await fetchRedditPostsApi(ticker)) ?? [];
});

const initialState = {
  ranking: [],
  rankingStatus: 'idle', // idle | loading | succeeded | failed
  ticker: null,
  verlauf: [],
  verlaufStatus: 'idle',
  subreddits: [],
  subredditsStatus: 'idle',
  posts: [],
  postsStatus: 'idle',
  error: null,
};

const redditSlice = createSlice({
  name: 'reddit',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRedditRanking.pending, (state) => {
        state.rankingStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchRedditRanking.fulfilled, (state, action) => {
        state.rankingStatus = 'succeeded';
        state.ranking = action.payload;
      })
      .addCase(fetchRedditRanking.rejected, (state, action) => {
        state.rankingStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchRedditHistory.pending, (state) => {
        state.verlaufStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchRedditHistory.fulfilled, (state, action) => {
        state.verlaufStatus = 'succeeded';
        state.ticker = action.payload.ticker;
        state.verlauf = action.payload.verlauf;
      })
      .addCase(fetchRedditHistory.rejected, (state, action) => {
        state.verlaufStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchRedditSubreddits.pending, (state) => {
        state.subredditsStatus = 'loading';
      })
      .addCase(fetchRedditSubreddits.fulfilled, (state, action) => {
        state.subredditsStatus = 'succeeded';
        state.subreddits = action.payload;
      })
      .addCase(fetchRedditSubreddits.rejected, (state) => {
        state.subredditsStatus = 'failed';
      })
      .addCase(fetchRedditPosts.pending, (state) => {
        state.postsStatus = 'loading';
      })
      .addCase(fetchRedditPosts.fulfilled, (state, action) => {
        state.postsStatus = 'succeeded';
        state.posts = action.payload;
      })
      .addCase(fetchRedditPosts.rejected, (state) => {
        state.postsStatus = 'failed';
      });
  },
});

export const selectRedditRanking = (state) => state.reddit.ranking;
export const selectRedditRankingStatus = (state) => state.reddit.rankingStatus;
export const selectRedditVerlauf = (state) => state.reddit.verlauf;
export const selectRedditVerlaufStatus = (state) => state.reddit.verlaufStatus;
export const selectRedditSubreddits = (state) => state.reddit.subreddits;
export const selectRedditSubredditsStatus = (state) => state.reddit.subredditsStatus;
export const selectRedditPosts = (state) => state.reddit.posts;
export const selectRedditPostsStatus = (state) => state.reddit.postsStatus;

export default redditSlice.reducer;
